import { PageError, PageLoader } from "@/components/common";
import { Button, Card, CardContent } from "@/components/ui";
import { useSettings } from "@/features/settings/api";
import { formatCurrency, formatDateTime } from "@/lib/utils";
import type { Order } from "@/types";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrderInvoice } from "../api";
import {
    buildReceiptMeta,
    renderReceiptDocument,
    type ReceiptMeta,
} from "../lib/receipt";

export function InvoicePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: order, isLoading, error } = useOrderInvoice(id || "");
  const { data: settings } = useSettings();

  if (isLoading) return <PageLoader />;
  if (error || !order) {
    return (
      <PageError
        title="Order not found"
        message="The order you're looking for doesn't exist"
        onRetry={() => navigate("/orders")}
      />
    );
  }

  const receipt = buildReceiptMeta(settings);
  const currencySymbol = receipt.currency;

  const hasAdjustments = order.discountAmount > 0 || order.tipAmount > 0;

  const handlePrint = () => {
    printReceipt(order, receipt);
  };

  return (
    <div className="container mx-auto max-w-md px-4 py-4 sm:py-6">
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handlePrint}>
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
      </div>
      <Card className="mx-auto max-w-sm font-mono">
        <CardContent className="space-y-3 p-6 text-[13px] leading-tight">
          <div className="text-center">
            <h1 className="text-base font-bold uppercase tracking-wide">
              {receipt.shopName}
            </h1>
            {receipt.shopAddress && <p>{receipt.shopAddress}</p>}
            {receipt.shopPhone && <p>{receipt.shopPhone}</p>}
          </div>
          <Dashed />
          <div>
            <Row label="Order" value={`#${order.orderNumber}`} bold />
            <Row label="Date" value={formatDateTime(order.createdAt)} />
            <Row label="Type" value={order.orderType.replace("_", " ")} />
            {order.customer && (
              <Row label="Customer" value={order.customer.name} />
            )}
            {order.user && <Row label="Cashier" value={order.user.firstName} />}
          </div>
          <Dashed />
          <div className="space-y-1">
            {order.items.map((item) => {
              const lineTotal = item.unitPrice * item.quantity;

              return (
                <div key={item.id}>
                  <Row
                    label={`${item.quantity}x ${item.productName}${item.variantName ? ` (${item.variantName})` : ""}`}
                    value={
                      item.isComped
                        ? formatCurrency(0, currencySymbol)
                        : formatCurrency(item.subtotal, currencySymbol)
                    }
                    strikeValue={
                      item.isComped
                        ? formatCurrency(lineTotal, currencySymbol)
                        : undefined
                    }
                  />
                  {item.options.map((opt) => (
                    <Row
                      key={opt.id}
                      label={`  + ${opt.optionName}`}
                      value={
                        opt.priceAdjustment > 0
                          ? `+${formatCurrency(opt.priceAdjustment, currencySymbol)}`
                          : ""
                      }
                      muted
                    />
                  ))}
                </div>
              );
            })}
          </div>
          <Dashed />
          <div>
            {hasAdjustments && (
              <>
                <Row
                  label="Subtotal"
                  value={formatCurrency(order.subtotal, currencySymbol)}
                />
                {order.discountAmount > 0 && (
                  <Row
                    label="Discount"
                    value={`-${formatCurrency(order.discountAmount, currencySymbol)}`}
                  />
                )}
                {order.tipAmount > 0 && (
                  <Row
                    label="Tip"
                    value={formatCurrency(order.tipAmount, currencySymbol)}
                  />
                )}
                <Dashed />
              </>
            )}
            <Row
              label="TOTAL"
              value={formatCurrency(order.total, currencySymbol)}
              bold
              large
            />
          </div>
          {order.payments.length > 0 && (
            <>
              <Dashed />
              <div>
                {order.payments.map((p) => (
                  <Row
                    key={p.id}
                    label={p.method.toUpperCase()}
                    value={formatCurrency(p.amountTendered, currencySymbol)}
                  />
                ))}
                {order.payments.some((p) => p.changeGiven > 0) && (
                  <Row
                    label="Change"
                    value={formatCurrency(
                      order.payments.reduce((s, p) => s + p.changeGiven, 0),
                      currencySymbol,
                    )}
                  />
                )}
              </div>
            </>
          )}

          <Dashed />
          <div className="space-y-1 text-center">
            <p>{receipt.footer}</p>
            {receipt.social && <p>{receipt.social}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Dashed() {
  return <div className="border-t border-dashed border-foreground/40" />;
}

function Row({
  label,
  value,
  strikeValue,
  bold,
  large,
  muted,
}: {
  label: string;
  value: string;
  strikeValue?: string;
  bold?: boolean;
  large?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={[
        "flex justify-between whitespace-pre",
        bold ? "font-bold" : "",
        large ? "text-base" : "",
        muted ? "text-muted-foreground text-xs" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span>{label}</span>
      <span className="flex items-center gap-1">
        {strikeValue && (
          <span className="text-muted-foreground line-through">
            {strikeValue}
          </span>
        )}
        <span className={strikeValue ? "text-green-600 font-semibold" : ""}>
          {value}
        </span>
      </span>
    </div>
  );
}

function printReceipt(order: Order, meta: ReceiptMeta) {
  const html = renderReceiptDocument(order, meta);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;left:0;top:0;width:0;height:0;border:0;opacity:0;pointer-events:none;z-index:-1";

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.open(url, "_blank");
    }

    setTimeout(() => {
      iframe.remove();
      URL.revokeObjectURL(url);
    }, 30000);
  };

  iframe.src = url;
  document.body.appendChild(iframe);
}
